import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { Button, ConfirmButton, LoadingIndicator } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import {
  Checkbox,
  Form,
  FormGroup,
  FormSection,
  LanguageVariants,
  Select,
  Text,
  TextArea
} from 'components/common/form';
import { CompetenciesFormLevels } from '.';

import type { FormApi } from 'informed';
import type { GetCompetenciesRes } from 'shared-types/responses';
import type { UpdateCompetencyReq } from 'shared-types/requests';

const CompetenciesCompetency = () => {
  const { query } = useRouter();

  const confirmLevelsChange = !query.new;

  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formApi = useRef<FormApi>(undefined);

  const { data, loading } = useApiData<GetCompetenciesRes>({
    onlyIf: Boolean(query.competencyId),
    url: `/admin/competencies?id=${query.competencyId}`
  });

  const competency = data?.competencyObjs?.[0];
  const categories = data?.categories;

  const handleLevels = (value: boolean) => {
    if (!value) formApi.current.setValue('levels', []);
  };

  const handleSubmit = async (values?: UpdateCompetencyReq) => {
    try {
      setIsSubmitting(true);

      await api.post(`/admin/competencies/${competency.id}`, values);

      dispatch(
        actions.addNotice({
          type: 'success',
          text: messages.competenciesEditSuccess
        })
      );

      setIsSubmitting(false);

      return true;
    } catch (err) {
      setIsSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = async (value: string) => {
    if (!value?.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/competencies?name=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <div className="competency">
      {loading ? (
        <LoadingIndicator />
      ) : !competency ? (
        <div>Error</div>
      ) : (
        <Box
          headerContent={
            <BoxBackLink
              as="/users/competencies"
              href="/users/competencies"
              text={messages.analyticsCompetenciesAll}
            />
          }
          padding={0}
        >
          <Form
            allowEmptyStrings={true}
            getApi={(form) => (formApi.current = form)}
            initialValues={{
              allowUserSelfAdding: competency.allowUserSelfAdding,
              categories: competency.categories,
              description: competency.description,
              descriptionLocales: competency.descriptionLocales,
              enableLevels: competency.enableLevels,
              levels: competency.levels?.length
                ? competency.levels
                : ([{ name: '' }] as UpdateCompetencyReq['levels']),
              name: competency.name,
              nameLocales: competency.nameLocales
            }}
          >
            {({ formState }) => (
              <div>
                <FormSection>
                  <FormGroup label={messages.commonTitle}>
                    <Text
                      customValidateAsync={validateTitle}
                      field="name"
                      id="name"
                      required={true}
                      validateOnBlur={true}
                      validateOnChange={true}
                    />

                    <LanguageVariants field="nameLocales" type="text" />
                  </FormGroup>

                  <FormGroup label={messages.commonDescription}>
                    <TextArea
                      field="description"
                      id="description"
                      validateOnChange={true}
                    />

                    <LanguageVariants
                      field="descriptionLocales"
                      type="textarea"
                    />
                  </FormGroup>

                  <FormGroup label={messages.commonCategories}>
                    <Select
                      creatable={true}
                      field="categories"
                      id="categories"
                      isMulti={true}
                      options={categories.map((category) => ({
                        label: category,
                        value: category
                      }))}
                    />
                  </FormGroup>
                </FormSection>

                <FormSection title={messages.competenciesAllowUsers}>
                  <Checkbox
                    field="allowUserSelfAdding"
                    id="allow-user-self-adding"
                    label={messages.competenciesAllowUsersText}
                  />
                </FormSection>

                <FormSection maxWidth="100%" title={messages.commonLevels}>
                  <Checkbox
                    field="enableLevels"
                    id="enable-levels"
                    label={messages.competenciesLevelsEnable}
                    onChange={(ev) =>
                      handleLevels((ev.target as HTMLInputElement).checked)
                    }
                  />

                  {formState.values.enableLevels ? (
                    <CompetenciesFormLevels />
                  ) : null}
                </FormSection>

                <FormSection>
                  {confirmLevelsChange &&
                  ((!formState.values.enableLevels &&
                    competency.enableLevels) ||
                    (formState.values.enableLevels &&
                      !competency.enableLevels) ||
                    competency.levels.some(
                      (level) =>
                        !formState.values.levels?.some(
                          (formStateLevel) => level.id === formStateLevel.id
                        )
                    )) ? (
                    <ConfirmButton
                      actionText={messages.commonConfirm}
                      buttonText={messages.commonSave}
                      category="success"
                      confirmText={
                        !formState.values.enableLevels &&
                        competency.enableLevels
                          ? messages.competenciesWarningDisableLevels
                          : formState.values.enableLevels &&
                            !competency.enableLevels
                          ? messages.competenciesWarningEnableLevels
                          : messages.competenciesWarningRemoveLevels
                      }
                      confirmTitle={messages.commonSaveChanges}
                      onConfirm={() => handleSubmit(formState.values)}
                    />
                  ) : (
                    <Button
                      category="success"
                      disabled={formState.invalid}
                      loading={isSubmitting}
                      onClick={() => handleSubmit(formState.values)}
                      text={messages.commonSave}
                    />
                  )}
                </FormSection>
              </div>
            )}
          </Form>
        </Box>
      )}
    </div>
  );
};

export default CompetenciesCompetency;
